import axios from "axios";
import { convertDistance, getDistance as getGeolibDistance } from "geolib";
import { numberService } from "../number/services.js";
import { classifyGeocodeRejection, } from "./geocodeRejectionReason.js";
/**
 * Seuil historique du score IGN (#510/#521/#532) — remplacé comme critère de
 * fiabilité par `isReliableGeocode` (#797, `docs/context/geocodage.md` §5),
 * mais encore lu par la migration de downgrade (#523) et plusieurs surfaces
 * de géocodage public/onboarding. Ne pas retirer sans auditer ces appelants.
 */
export const SCORE_LIMIT = 0.6;
/**
 * Une coordonnée n'est exploitable que si elle est finie. `Number.isFinite`
 * rejette déjà `null`/`undefined` sans coercition ; le prédicat de type narrow
 * en prime, ce qu'un test de finitude nu ne fait pas.
 */
const isUsableCoordinate = (value) => Number.isFinite(value);
/**
 * Implémentation partagée par les deux formes exportées ci-dessous — la
 * doctrine du critère est documentée sur `isReliableGeocode`, le nom cité par
 * `docs/context/geocodage.md`. Reste privée tant qu'aucun appelant n'a besoin
 * du point ET d'une `precision` (les fiches reconnues le pourraient déjà mais
 * gardent leur parse maison — à unifier avec #807).
 */
const resolveReliablePoint = (precision, lat, lng) => {
    // Le seul refus porte sur `municipality`. `"unknown"` (#807) passe donc, au
    // même titre que `null` : c'est un doute sur la finesse, pas une certitude
    // que la position est mauvaise. Ne PAS l'ajouter à ce test — ce serait
    // refuser des adresses résolues dont l'IGN n'a simplement pas donné le niveau.
    if (!isUsableCoordinate(lat) || !isUsableCoordinate(lng) || precision === "municipality") {
        return null;
    }
    return { lat, lng };
};
/**
 * Critère unique de fiabilité géographique (#797, voir `docs/context/geocodage.md`
 * §1 et §5) : on ne refuse que sur certitude négative, jamais sur un doute.
 *
 * 1. Pas de coordonnées → refusé : rien à exploiter.
 * 2. `municipality` → refusé : centroïde de la commune, pas une adresse.
 * 3. Tout le reste accepté, `null` compris — traiter `null` comme un refus
 *    serait une régression : 23 fiches avec coordonnées valides et
 *    `precision = null` passent aujourd'hui grâce à leur score et doivent
 *    continuer à passer.
 *
 * « Pas de coordonnées » signifie non finies, pas seulement absentes : les
 * colonnes `customer.service_lat`/`service_long` sont des varchar, donc une
 * fiche antérieure au garde-fou de `apiIgn` peut porter la string "NaN".
 */
export const isReliableGeocode = (precision, lat, lng) => resolveReliablePoint(precision, lat, lng) !== null;
/**
 * Variante du critère pour un chemin d'écriture public dont le payload ne porte
 * pas de granularité (`resolveBodyLocation` de `/proposals` — #796) : dégénère
 * en un test de présence de coordonnées, seule certitude négative disponible
 * sans `precision`. Rend le point plutôt qu'un booléen — l'appelant qui le
 * persiste le récupère narrowé, sans cast.
 *
 * `requireGeocodedAddressFromPayload` ne s'en contente plus depuis #807 : son
 * payload porte la granularité, il applique donc `isReliableGeocode` en amont.
 */
export const resolveReliablePointWithoutPrecision = (lat, lng) => resolveReliablePoint(null, lat, lng);
/** Articles retirés des DEUX côtés du containment — un nom propre qui les contient (« la Colle ») n'en souffre pas, ils disparaissent symétriquement. */
const STOPWORD_TOKENS = new Set(["de", "des", "du", "la", "le", "l", "et"]);
/** Dictionnaire d'abréviations voirie (#920) — un seul sens, abrégé → développé, jamais l'inverse. */
const VOIE_ABBREVIATIONS = {
    av: "avenue",
    bd: "boulevard",
    r: "rue",
    st: "saint",
    ste: "sainte",
    pl: "place",
    imp: "impasse",
    rte: "route",
    chem: "chemin",
};
/**
 * Nettoyage bas niveau d'un composant d'adresse : accents (NFD), casse
 * (fr-FR), ponctuation/tirets/apostrophes → espaces, espaces multiples
 * repliés, trim. Partagé entre `tokenizeAddress` (containment rue) et
 * `normalizeAddressComponent` (`packages-web/ui`, comparaison/surlignage
 * zip+ville) — les deux DOIVENT rester sur ce même nettoyage : un
 * normaliseur qui replie les tirets/la ponctuation et l'autre pas fait
 * passer un simple tiret (« Ernolsheim lès Saverne » vs
 * « Ernolsheim-lès-Saverne ») pour une divergence côté zip/ville (#1008).
 */
export function normalizeAddressText(value) {
    return (value
        .normalize("NFD")
        // biome-ignore lint/suspicious/noMisleadingCharacterClass: plage Unicode des marques diacritiques combinantes (idiome NFD standard), pas un caractere + combinant piegeux.
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase("fr-FR")
        .replace(/['’\-.,]/g, " ")
        .replace(/\s+/g, " ")
        .trim());
}
/**
 * Mentions de répétition couvertes (#1078) : bis/ter/quater, observées dans
 * l'étude #1037 ; `quinquies` non observé, à ajouter seulement s'il apparaît.
 * COLLE un numéro et sa mention séparés ("64 bis" → "64bis"), jamais
 * l'inverse : coller ne peut fabriquer un token que l'autre côté n'a pas,
 * donc aucun ancrage de position n'est nécessaire — un numéro déjà collé
 * ("64bis") ou une mention isolée sans numéro adjacent ("Rue du Bis")
 * traversent le remplacement inchangés, où qu'ils apparaissent dans la
 * chaîne. Détacher (la direction inverse) casse cette propriété : un
 * numéro non en tête de la saisie (nom de résidence en préfixe d'`address`)
 * désynchronise alors les deux côtés du containment.
 */
const HOUSE_NUMBER_SUFFIX_GLUE_PATTERN = /(\d+) ?(bis|ter|quater)\b/g;
/**
 * Normalise une adresse en un ensemble de tokens comparables : nettoyage bas
 * niveau (`normalizeAddressText`) puis abréviations développées, articles
 * retirés. PAS de distance d'édition — elle réintroduirait un curseur de
 * tolérance, et une faute de frappe mérite la question (#920).
 *
 * Un `Set`, pas un multiset : le containment ne porte que sur la présence de
 * chaque token du résolu dans la saisie, jamais sur ses répétitions.
 *
 * Colle un numéro et sa mention bis/ter/quater (#1078), pour que "64 bis" et
 * "64bis" tokenisent au même token "64bis".
 *
 * Exportée (#1079) pour la comparaison de tokens de ville de l'auto-correction
 * classe 3 (`packages-api/platform-server/src/geo/addressResyncPass.ts`) —
 * même normalisation que le containment ci-dessus, pas une seconde version.
 */
export const tokenizeAddress = (value) => {
    const tokens = normalizeAddressText(value)
        .replace(HOUSE_NUMBER_SUFFIX_GLUE_PATTERN, "$1$2")
        .split(" ")
        .filter(Boolean)
        .map((token) => VOIE_ABBREVIATIONS[token] ?? token)
        .filter((token) => !STOPWORD_TOKENS.has(token));
    return new Set(tokens);
};
/**
 * Numéro en tête d'une chaîne (ex. "12" dans "12 rue de la Paix"), ou `null`
 * si elle n'en commence pas par un. Matche sur le texte normalisé (pas la
 * chaîne brute) pour rester cohérent avec `tokenizeAddress` — un séparateur
 * de tête (virgule, tirets) que `normalizeAddressText` replie autrement
 * masquerait un numéro pourtant bien en position 0 (#1078).
 */
const leadingHouseNumber = (value) => normalizeAddressText(value).match(/^(\d+)/)?.[1] ?? null;
/**
 * Faut-il demander une confirmation bloquante à l'utilisateur avant d'accepter
 * cette position (#864, algorithme #920) ? Question distincte
 * d'`isReliableGeocode`, à dessein : les deux ne se recoupent pas.
 * `isReliableGeocode` décide si une position est EXPLOITABLE pour la
 * facturation/tournées (elle accepte tout doute) ; celle-ci décide si la
 * saisie MÉRITE d'être revérifiée par l'utilisateur.
 *
 * Le score sort entièrement de la décision (#920) : il confond similarité
 * textuelle et `importance` du lieu, et ne mesure pas la fidélité du résolu à
 * la saisie. Le critère est un containment `resolvedTokens ⊆ saisieTokens` —
 * jamais l'inverse, la saisie a le droit d'être plus riche que le résolu
 * (piège « écurie de l'arros », `docs/context/geocodage.md` piège 2) sans
 * qu'aucun tolérance/curseur ne soit nécessaire pour l'absorber.
 *
 * Une seule règle sur `customer` et `address` depuis #920 : la dérogation
 * `applyLocalityToponymThreshold` (seuil non calibrable sur les 25 lignes
 * `address`) disparaît avec le score qui la justifiait.
 */
export const needsGeocodeConfirmation = (precision, resolvedName, saisie) => {
    if (precision !== "housenumber" &&
        precision !== "street" &&
        precision !== "locality" &&
        precision !== "toponym") {
        /**
         * municipality, unknown, precision null/undefined (jamais mesurée) :
         * confirmation systématique — on ne sait pas, ou on est certain que ce
         * n'est pas fiable.
         */
        return true;
    }
    const resolvedTokens = tokenizeAddress(resolvedName);
    if (resolvedTokens.size === 0) {
        /**
         * Résolu vide (IGN sans `name` ni `toponym`, cas résiduel défensif) :
         * le containment du vide serait toujours vrai — confirmer plutôt que de
         * valider silencieusement sur exactement le cas où l'on a le moins
         * d'information.
         */
        return true;
    }
    if ((precision === "locality" || precision === "toponym") && resolvedTokens.size === 1) {
        /**
         * Un seul token distinctif après retrait des articles (ex. "Le Bourg" →
         * {bourg}) : trivialement contenu dans toute saisie qui mentionne juste
         * ce mot, y compris pour désigner un AUTRE lieu ("12 rue du Bourg").
         * Même doctrine que le résolu vide ci-dessus — un mot seul ne porte pas
         * assez d'information pour ces deux granularités. C'est précisément ce
         * qui gardait l'adresse de départ en confirmation systématique avant
         * #920 (`applyLocalityToponymThreshold=false`) ; housenumber/street n'y
         * sont volontairement PAS soumis, un nom de rue à un seul mot ("Lambouise")
         * étant un cas nominal, pas un doute.
         */
        return true;
    }
    if (precision === "housenumber") {
        /**
         * Le containment seul peut se faire absorber par un numéro parasite du
         * complément d'adresse (ex. saisie "3 rue x, Appartement 12" contient le
         * token "12" alors que le numéro RÉELLEMENT saisi est "3") — comparer le
         * numéro de tête de la saisie complète, qui correspond à celui du champ
         * adresse (`address`) puisque `buildGeocodeInputText` place `line1` en
         * premier. Un mauvais numéro est la mauvaise porte, même gravité qu'une
         * mauvaise rue. Scopé à `housenumber` :
         * sur les autres granularités, un numéro de tête peut faire partie du nom
         * du lieu-dit/de la rue lui-même (ex. "4 Chemins"), pas d'un numéro de
         * porte à vérifier — le containment générique ci-dessous suffit, la
         * mention bis/ter/quater y est déjà collée symétriquement par
         * `tokenizeAddress`.
         */
        const resolvedNumber = leadingHouseNumber(resolvedName);
        const saisieNumber = leadingHouseNumber(saisie);
        if (resolvedNumber != null && saisieNumber != null && resolvedNumber !== saisieNumber) {
            return true;
        }
    }
    /**
     * Containment par `Set`, insensible à l'ordre des tokens : accepté tel
     * quel, faute de mesure prouvant un faux négatif réel sur des adresses FR
     * (permutation de mots type « rue de la Paix » vs « Paix de la rue »),
     * doctrine « mesurer avant de contraindre » (piège 2, docs/context/geocodage.md).
     */
    const saisieTokens = tokenizeAddress(saisie);
    for (const token of resolvedTokens) {
        if (!saisieTokens.has(token)) {
            return true;
        }
    }
    return false;
};
/**
 * Égalité exacte texte normalisé, `locality`/`toponym` seulement (#1082) —
 * bypass ciblé du garde mono-token de `needsGeocodeConfirmation` ci-dessus :
 * un lieu-dit à un seul mot distinctif ("Le Bourg") y confirme TOUJOURS, y
 * compris sur une correspondance exacte, faute d'information suffisante pour
 * le containment seul. Cette fonction ajoute un chemin plus fort à côté,
 * elle ne retire rien au garde — une saisie strictement identique au résolu
 * (normalisation bas niveau `normalizeAddressText` : accents, casse,
 * ponctuation) ne laisse plus de doute à lever, mono-token ou non.
 * `housenumber`/`street` n'ont pas besoin de ce chemin : le garde qu'il
 * contourne ne les concerne pas (voir le commentaire sur `needsGeocodeConfirmation`).
 */
export const isExactLocalityMatch = (precision, resolvedName, saisie) => (precision === "locality" || precision === "toponym") &&
    normalizeAddressText(resolvedName) === normalizeAddressText(saisie);
/**
 * Extrait le nom de voie/lieu résolu d'un `service_geocode_label` STOCKÉ,
 * ancré sur le code postal — grammaire IGN `"{voie-ou-lieu-dit} {cp 5
 * chiffres} {ville}"`, séparateur espace, jamais de virgule, vérifiée à
 * 100 % sur les fixtures IGN du repo et `setCustomerGeocode` (#1086,
 * `docs/context/geocodage.md` §1 « Lieux-dits reconnus »). Alimente
 * `needsGeocodeConfirmation`/`isExactLocalityMatch` sans stocker de colonne
 * dédiée (`resolvedName` n'est jamais persisté, seul `label` l'est).
 *
 * Rend `""` (jamais `null`) sur label absent/vide/mal formé, ou si le CP
 * extrait diverge du CP stocké (homonymie CP non résolue ici, résidu accepté
 * — #1086) : `needsGeocodeConfirmation` traite déjà un résolu vide comme
 * « à confirmer », donc une extraction ratée retombe sur le comportement
 * prudent plutôt que de propager un nom erroné.
 *
 * Comparaison CP au sens des ESPACES INTERNES retirés (`stripZipWhitespace`,
 * même geste que `customerFormSchema.ts`/`apps/api/src/scripts/
 * addressResyncPass.ts`) : un CP legacy stocké espacé (`"95 800"`) doit
 * matcher `"95800"` — `normalizeAddressText` seul ne suffit pas ici, il
 * replie les espaces multiples mais ne retire pas un espace interne unique.
 */
export function deriveResolvedNameFromLabel(label, storedZip) {
    if (!label) {
        return "";
    }
    const match = label.match(/^(.*?)\s(\d{5})\s+(.+)$/);
    if (!match) {
        return "";
    }
    const [, name, extractedZip] = match;
    const stripZipWhitespace = (zip) => zip.replace(/\s+/g, "");
    if (stripZipWhitespace(normalizeAddressText(extractedZip ?? "")) !==
        stripZipWhitespace(normalizeAddressText(storedZip ?? ""))) {
        return "";
    }
    return (name ?? "").trim();
}
export const getDistance = (autoDistance, manualDistance) => {
    let result = autoDistance !== null ? autoDistance : 0;
    if (manualDistance || manualDistance === 0) {
        result = manualDistance;
    }
    return Math.max(0, numberService.floatValue(result)); // avoid negative result
};
const normalizeAddressField = (value) => (value ?? "").trim().toLocaleLowerCase("fr-FR").replace(/\s+/g, " ");
// Retire le suffixe structuré `", cp ville"` d'une adresse brute (#661), pour n'en garder que le champ rue.
export const stripStructuredSuffix = (address, zip, city) => {
    if (!zip || !city) {
        return address;
    }
    const suffix = `, ${zip} ${city}`;
    return address.endsWith(suffix) ? address.slice(0, -suffix.length) : address;
};
/**
 * « L'adresse a réellement changé » (#799) : compare les 4 champs après
 * normalisation (trim, casse repliée, espaces multiples réduits) pour qu'un
 * ré-envoi du même formulaire, ou une simple différence de casse/espacement,
 * ne déclenche pas un appel IGN inutile ni un recalcul de position.
 */
export const hasAddressChanged = (before, after) => normalizeAddressField(before.line1) !== normalizeAddressField(after.line1) ||
    normalizeAddressField(before.line2) !== normalizeAddressField(after.line2) ||
    normalizeAddressField(before.zip) !== normalizeAddressField(after.zip) ||
    normalizeAddressField(before.city) !== normalizeAddressField(after.city);
/**
 * Côté « saisie » du containment de `needsGeocodeConfirmation` (#920) — la
 * saisie complète (adresse + complément), indépendante du texte EFFECTIVEMENT
 * envoyé à l'IGN sur la requête qui a produit le résultat comparé (`apiIgn`,
 * `geo.service.ts`, deux passes depuis #991 — address2 n'y figure que si la
 * passe 1 a échoué) : le containment compare ce que l'utilisateur a tapé au
 * nom résolu, une question distincte du texte réellement interrogé.
 * Centralisé pour ne pas le dupliquer entre `useAddressVerification.ts`
 * (hook FE partagé par les 4 surfaces) et `addressResyncPass.ts` (même
 * verdict de classification côté batch).
 */
export const buildGeocodeInputText = (fields) => fields.line2 ? `${fields.line1} ${fields.line2}` : fields.line1;
/**
 * Borne de cohérence commune (#799 D2/D5) : distance maximale acceptée entre un
 * pin manuel et le centroïde IGN de la commune/CP saisis — 25 km, pas les
 * 15-20 km de l'estimation initiale. Vérifié en étude sur Arles (758 km²,
 * commune la plus étendue de France métropolitaine) : des hameaux réels y sont
 * à ~28 km de son centroïde, et une borne à 20 km rejetterait un pin légitime
 * (doctrine « écurie de l'arros », faux positif déjà connu du domaine).
 */
export const RDV_MANUAL_PIN_MAX_DISTANCE_KM = 25;
/**
 * Vrai si `pin` est à moins de `RDV_MANUAL_PIN_MAX_DISTANCE_KM` du centroïde de
 * la commune (#799) — le seul garde-fou possible contre un point posé n'importe
 * où sur la carte, en l'absence de toute vérification IGN de l'adresse elle-même.
 *
 * Distance calculée en direct (PAS `birdFlightDistanceKm`) :
 * ce dernier passe par `numberService.normalizeQuantity`, qui ARRONDIT au km entier
 * (correct pour l'affichage zone/facturation, son usage d'origine) et convertit tout
 * `NaN` en `0` — un point à coordonnées non finies passerait alors la borne (`0 <=
 * 25`). Ici la borne doit être stricte : coordonnée non finie → refusé explicitement,
 * distance comparée sans arrondi (un point à 25,4 km ne doit pas passer pour 25).
 */
export const isWithinCommuneCoherenceBound = (pin, communeCentroid) => {
    if (!Number.isFinite(pin.latitude) ||
        !Number.isFinite(pin.longitude) ||
        !Number.isFinite(communeCentroid.latitude) ||
        !Number.isFinite(communeCentroid.longitude)) {
        return false;
    }
    const meters = getGeolibDistance(pin, communeCentroid);
    return meters / 1000 <= RDV_MANUAL_PIN_MAX_DISTANCE_KM;
};
export const setCustomerGeocode = (customer, geocode) => {
    if (geocode) {
        customer.serviceLong = `${geocode.longitude}`;
        customer.serviceLat = `${geocode.latitude}`;
        customer.serviceGeocodeScore = `${geocode.score}`;
        customer.serviceGeocodePrecision = geocode.precision;
        customer.serviceGeocodeLabel = geocode.label;
    }
    else {
        customer.serviceLong = undefined;
        customer.serviceLat = undefined;
        customer.serviceGeocodeScore = undefined;
        customer.serviceGeocodePrecision = undefined;
        customer.serviceGeocodeLabel = undefined;
    }
};
/**
 * Bird-flight (great-circle) distance in km between two GPS points, via geolib.
 *
 * Reusable, ORS-free pre-filter (NFR2): a cheap geolib estimate to short-circuit
 * before any OpenRouteService routing call. Both `computeZoneDistance` (shifting
 * fees) and the RDV engine's travel provider (2.5) build on it.
 */
export const birdFlightDistanceKm = (from, to) => numberService.normalizeQuantity(convertDistance(getGeolibDistance(from, to), "km"));
export const computeZoneDistance = (departure, customer) => {
    const customerPosition = getCustomerPosition(customer);
    return customerPosition ? birdFlightDistanceKm(departure, customerPosition) : 0;
};
/**
 * Typed seam member → ZONE distance: bird-flight km from the member's departure
 * address to the customer (#520). The member is a real `TeamMember` (non-null);
 * `getMemberPosition` is the single place a missing/un-geocoded departure
 * surfaces as `null` → 0 km. The runtime guarantee that an active member is
 * always geocoded lands with the activation guard (#521) + migration (#523).
 */
export const computeMemberZoneDistance = (member, customer) => {
    const departure = getMemberPosition(member);
    return departure ? computeZoneDistance(departure, customer) : 0;
};
/**
 * Verdict de fiabilité d'une position ZONE pour la facturation (#838, #843
 * volets D et E) : rend le bout du trajet en cause et pourquoi, ou `null` si
 * les deux bouts sont exploitables.
 *
 * Source unique — le serveur le calcule une fois (`resolveEventShifting`) puis
 * le TRANSPORTE jusqu'au front, qui ne le recalcule plus. Le volet D avait
 * partagé la règle sans partager le verdict : le front la ré-appliquait sur un
 * `member` résolu via `GET /api/team/members`, qui exclut les membres
 * soft-deleted et ceux dont l'utilisateur n'est pas activé, là où le serveur
 * lit `event.member` sans filtre. Sur un tel événement, les deux ne POUVAIENT
 * pas s'accorder — même règle, entrées différentes.
 *
 * Ordre d'évaluation volontaire (membre avant client) : c'est celui qui décide
 * quel message s'affiche quand les deux bouts sont mauvais. Le départ est
 * signalé en premier parce que seul le manager peut le corriger, alors qu'un
 * intervenant peut corriger la fiche client lui-même.
 */
export const resolveZoneShiftingUnreliablePosition = (member, customer) => {
    if (!member) {
        return { endpoint: "unassigned", reason: "not-found" };
    }
    const { latitude, longitude, geocodePrecision } = member.address;
    if (!isReliableGeocode(geocodePrecision, latitude, longitude)) {
        return {
            endpoint: "member",
            reason: classifyGeocodeRejection(geocodePrecision, latitude, longitude),
        };
    }
    if (!customer) {
        return { endpoint: "customer", reason: "not-found" };
    }
    const position = getCustomerPosition(customer);
    if (!isReliableGeocode(customer.serviceGeocodePrecision, position?.latitude, position?.longitude)) {
        return {
            endpoint: "customer",
            reason: classifyGeocodeRejection(customer.serviceGeocodePrecision, position?.latitude, position?.longitude),
        };
    }
    return null;
};
/** Cause de rejet d'une position client, ou `null` si elle est exploitable. */
const classifyCustomerRejection = (customer) => {
    const position = getCustomerPosition(customer);
    if (isReliableGeocode(customer.serviceGeocodePrecision, position?.latitude, position?.longitude)) {
        return null;
    }
    return classifyGeocodeRejection(customer.serviceGeocodePrecision, position?.latitude, position?.longitude);
};
/**
 * Pourquoi la passe de tournée REAL a sauté ce rendez-vous (#843 volet C), ou
 * `null` si rien de connu ne bloque (la tournée n'a simplement pas tourné).
 *
 * Reproduit délibérément l'ordre des sorties de `updateWorkerTour`
 * (`custom.service.ts`) — départ d'abord, puis les clients de la journée : un
 * message qui n'annoncerait pas la MÊME cause que celle qui a fait sauter le
 * calcul serait pire que pas de message.
 *
 * La seule chose que la passe ne distingue pas et que ce résolveur distingue :
 * elle teste `events.some(invalidToComputeInTour)` sans regarder QUEL client
 * est fautif. Séparer « le vôtre » de « celui d'un autre rendez-vous » est tout
 * l'objet du volet C — c'est ce qui décide si l'action proposée peut aboutir.
 *
 * Conséquence assumée (harden round 2) : quand le client courant ET un voisin
 * sont fautifs, on n'annonce que le premier. Corriger sa propre fiche ne
 * débloquera donc pas la tournée, et le message suivant désignera le voisin.
 * C'est une chaîne de corrections, pas un mensonge : chaque message est vrai au
 * moment où il s'affiche. Le sien passe en premier parce que c'est la seule
 * fiche que l'intervenant peut corriger sans aide.
 *
 * `sameDayCustomers` exclut le client de CE rendez-vous (l'appelant filtre) :
 * sans ça, un client fautif serait annoncé comme « un autre rendez-vous ».
 */
export const resolveTourSkipCause = (departure, customer, sameDayCustomers) => {
    const { geocodePrecision, latitude, longitude } = departure;
    if (!isReliableGeocode(geocodePrecision, latitude, longitude)) {
        return {
            endpoint: "member",
            reason: classifyGeocodeRejection(geocodePrecision, latitude, longitude),
        };
    }
    /**
     * Un client ABSENT est une cause, pas une absence de cause :
     * la passe abandonne sur `!event?.customer` au même titre que sur une adresse
     * inexploitable. Le rater rendait `null` sur 11 événements REAL de prod, donc
     * le message « recalculez la tournée » — l'action impossible que ce volet
     * existe pour supprimer.
     */
    const ownReason = customer ? classifyCustomerRejection(customer) : "no-customer";
    if (ownReason) {
        return { endpoint: "customer", reason: ownReason };
    }
    for (const sibling of sameDayCustomers) {
        const reason = sibling ? classifyCustomerRejection(sibling) : "no-customer";
        if (reason) {
            return { endpoint: "other-customer", reason };
        }
    }
    return null;
};
/**
 * Booléen dérivé du verdict ci-dessus, pour le chemin argent qui n'a besoin que
 * de « faut-il forcer 0€ » (`shouldForceZeroShiftingAmount`). Dérivé plutôt que
 * dupliqué : les deux ne peuvent pas répondre différemment.
 */
export const isZoneShiftingPositionUnreliable = (member, customer) => resolveZoneShiftingUnreliablePosition(member, customer) !== null;
/**
 * GPS departure point of a team member, read from their departureAddress
 * (DB `home_address`, exposed as `member.address`). Returns null when either
 * coordinate is missing.
 *
 * Note: does NOT gate on `geocodeScore` — symmetric with the legacy
 * `getPosition` company branch which never filtered on score either.
 *
 * Ne plus rien déduire du score sur la position rendue ici (#797 PR2) : le
 * guard d'activation amont (#521/#532) applique désormais `isReliableGeocode`,
 * qui accepte un score bas ou nul dès que la granularité est exploitable. Une
 * position lue ici peut donc venir d'une adresse à score 0,05 — seule la
 * migration de downgrade (#523) garantissait encore `>= SCORE_LIMIT`, et
 * uniquement sur l'historique qu'elle a traité.
 */
export const getMemberPosition = (member) => {
    const { latitude, longitude } = member.address;
    if (latitude != null && longitude != null) {
        return { latitude, longitude };
    }
    return null;
};
/**
 * GPS position of a customer's service location, read from the geocoded
 * `serviceLat`/`serviceLong`. Returns null when either coordinate is missing OR
 * does not parse to a finite number. Symmetric with `getMemberPosition`: the
 * ZONE money path (`quotation.service.ts`) reads it via `isReliableGeocode` to
 * decide whether the billed amount must be forced to 0€ (#838) — it no longer
 * refuses the quote outright (#532), but the finiteness test below still
 * matters just as much for that decision.
 *
 * Le test de finitude n'est pas cosmétique (#797) : ces colonnes sont des
 * varchar, un NaN s'y persiste en string "NaN", et un NaN qui traverse rend
 * `birdFlightDistanceKm` NaN — `getZone` retombe alors sur la première zone,
 * donc facture le trajet le moins cher, en silence — exactement ce que
 * `isReliableGeocode` doit détecter pour que #838 force 0€ au lieu de ça.
 */
export const getCustomerPosition = (customer) => {
    const longitude = parseStoredCoordinate(customer.serviceLong);
    const latitude = parseStoredCoordinate(customer.serviceLat);
    if (Number.isFinite(longitude) && Number.isFinite(latitude)) {
        return { longitude, latitude };
    }
    return null;
};
/**
 * Parse une coordonnée stockée en varchar (#797 PR3) :
 * 1. `Number.parseFloat("48.1junk")` vaut 48.1 — un préfixe numérique suffit,
 *    donc une valeur corrompue passait pour finie et se faisait facturer.
 * 2. `Number()` rejette la chaîne entière en `NaN`, mais rend 0 sur `""` ET sur
 *    une chaîne d'espaces — les deux doivent être écartées avant conversion,
 *    sinon elles deviennent la coordonnée 0, valide au sens du critère (le
 *    méridien de Greenwich), donc jamais rattrapée en aval.
 */
const parseStoredCoordinate = (value) => {
    const trimmed = value?.trim();
    return trimmed ? Number(trimmed) : Number.NaN;
};
/** The real OpenRouteService directions endpoint (production default). */
export const ORS_DIRECTIONS_URL = "https://api.heigit.org/openrouteservice/v2/directions/driving-car/geojson";
export const computeDistance = async (coordinates, openRouteKey, 
/**
 * Endpoint is injectable so the e2e harness can point the booted API at a
 * local mock ORS server (deterministic travel times, no network) — prod and
 * every existing caller keep the real default.
 */
baseUrl = ORS_DIRECTIONS_URL, signal) => {
    const openrouteData = {
        preference: "fastest",
        language: "fr",
        units: "km",
        instructions: false,
        elevation: false,
        coordinates,
        geometry_simplify: true,
    };
    try {
        const { data } = await axios.post(baseUrl, openrouteData, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `${openRouteKey}`,
            },
            /**
             * Cap the ORS wait (#621). Without a timeout a slow ORS response holds a
             * socket open for the full Node default, and a single RDV planning run
             * may open dozens of them in parallel — a socket exhaustion vector on the
             * public surface. Aligned with `fetchGeocode` (10s). Applies to every
             * caller (shifting fees + RDV): a call that pends past 10s has no value
             * for anyone.
             */
            timeout: 10_000,
            signal,
        });
        const { features: [{ properties: { summary: { distance, duration }, }, },], } = data;
        /**
         * Require BOTH metrics: a valid driving route always carries distance and
         * duration together. Returning a phantom 0 duration (from a missing field)
         * would silently mislead the time-based RDV scoring — better no result.
         */
        if (distance && distance > 0 && duration && duration > 0) {
            return {
                distance: numberService.normalize(distance),
                duration: numberService.normalize(duration),
            };
        }
    }
    catch (e) {
        if (!signal?.aborted) {
            console.error(e?.message);
        }
    }
};
export const computeEventCoordinates = (userLong, userLat, events) => {
    const userCoordinate = [userLong, userLat];
    const coordinates = [];
    let prevCoordinate = userCoordinate;
    for (const event of events) {
        if (event.customer) {
            const { serviceLong, serviceLat } = event.customer;
            const eventLong = serviceLong ? Number.parseFloat(serviceLong) : undefined;
            const eventLat = serviceLat ? Number.parseFloat(serviceLat) : undefined;
            if (eventLong && eventLat) {
                const nextCoordinate = [eventLong, eventLat];
                const [prevLong, prevLat] = prevCoordinate;
                if (eventLong !== prevLong || eventLat !== prevLat) {
                    coordinates.push(nextCoordinate);
                    prevCoordinate = nextCoordinate;
                } // else ignore
            }
        }
    }
    let finalCoordinates = [];
    if (coordinates.length > 0) {
        // wrap userCoordinate
        const firstCoordinate = coordinates[0];
        const lastCoordinate = coordinates[coordinates.length - 1];
        finalCoordinates = [...coordinates];
        if (firstCoordinate) {
            const [firstLong, firstLat] = firstCoordinate;
            if (userLong !== firstLong || userLat !== firstLat) {
                // Add user coordinate in first only if required
                finalCoordinates = [userCoordinate, ...finalCoordinates];
            }
        }
        if (lastCoordinate) {
            const [lastLong, lastLat] = lastCoordinate;
            if (userLong !== lastLong || userLat !== lastLat) {
                // Add user coordinate in last only if required
                finalCoordinates = [...finalCoordinates, userCoordinate];
            }
        }
    }
    return finalCoordinates;
};
// Zone services from zoneService.ts
export const getZone = (company, zoneAutoDistance, manualDistance) => {
    let result = null;
    const zones = getZones(company);
    if (zones.length > 0) {
        result = zones[0]; // init with the first zone available
        const distance = getDistance(zoneAutoDistance, manualDistance);
        for (let i = 0; i < zones.length; i++) {
            const aZone = zones[i];
            const min = i <= 0 ? 0 : zones[i - 1].distance;
            const max = aZone.distance;
            if (distance >= min) {
                if (!max || (max && distance < max)) {
                    result = aZone;
                    break;
                }
            }
        }
    }
    return result;
};
export const getZones = (company) => {
    const result = [];
    let buffer;
    if (company.shifting.shiftingZoneData?.type === "Buffer") {
        buffer = Buffer.from(company.shifting.shiftingZoneData?.data);
    }
    else if (company.shifting.shiftingZoneData instanceof Buffer) {
        buffer = company.shifting.shiftingZoneData;
    }
    else if (typeof company.shifting.shiftingZoneData === "string") {
        buffer = Buffer.from(company.shifting.shiftingZoneData);
    }
    if (buffer) {
        const shiftingZoneData = buffer.toString("utf8");
        const array = JSON.parse(shiftingZoneData);
        if (array?.length > 0) {
            for (const item of array) {
                if ("id" in item && typeof item.id !== "string") {
                    continue; // id required
                }
                if (("label" in item && !item.label) || typeof item.label !== "string") {
                    continue; // label required
                }
                if (("amount" in item && item.amount) || item.amount === 0) {
                    if (typeof item.amount !== "number" || Number.isNaN(item.amount) || item.amount < 0) {
                        continue; // amount is a required positive number
                    }
                }
                else {
                    continue;
                }
                if ("distance" in item) {
                    if (typeof item.distance !== "number" ||
                        Number.isNaN(item.distance) ||
                        item.distance < 0) {
                        continue; // distance is an optional positive number
                    }
                }
                result.push(item);
            }
        }
    }
    return result;
};
export const getDefaultZones = () => [
    { id: "0", label: "Zone 1", distance: 15, amount: 0 },
    { id: "1", label: "Zone 2", distance: 30, amount: 5 },
    { id: "2", label: "Zone 3", distance: 45, amount: 10 },
    { id: "3", label: "Zone 4", amount: 15 },
];
//# sourceMappingURL=services.js.map